import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  NLSFB,
  assetNameForCode,
  assetTypeForCode,
  nlsfb,
  nlsfbChildren,
  nlsfbGroupCode,
  nlsfbNode,
  nlsfbTrail,
  searchElements,
  NLSFB_SEP,
  type NlsfbNode,
} from "@klantpunt/shared";
import { AssetBadge } from "../assets/assetIcons";

/** Korte naam van een code: het laatste deel van het pad, met hoofdletter ("Warmtepompen"). */
export function elementShortName(code: string): string {
  const last = nlsfbTrail(code).at(-1) ?? code;
  return last.charAt(0).toUpperCase() + last.slice(1);
}

/** Pad boven de code: "Installaties werktuigbouwkundig › Verwarming en koeling › Opwekking centraal". */
function pathAbove(code: string): string {
  const { main } = nlsfb(code);
  const trail = nlsfbTrail(code).slice(0, -1);
  return [main?.label, ...trail.map((t) => t.charAt(0).toUpperCase() + t.slice(1))].filter(Boolean).join(NLSFB_SEP);
}

/** "x0 algemeen (verzamelniveau)" is hetzelfde als de groep erboven kiezen. */
const isCollective = (n: NlsfbNode) => n.level === 4 && /verzamelniveau/.test(n.label);
const isUnnamed = (label: string) => /^Hoger niet genoemd/i.test(label);

/**
 * "Wat is het?": één NL-SfB-kiezer (docs/SYMBOLEN.md, besluit 04-11-2026). Zoeken eerst; of bladeren
 * hoofdgroep › element › detail. Breed staan de lagen naast elkaar, smal ga je stap voor stap.
 * Het symbool hoort bij de code. Zonder volledige tabel (`full` uit) stopt het bij het element.
 */
export function ElementPicker({
  value,
  onChange,
  full,
  suggestions = [],
  recent = [],
  favorites = [],
  defaultOpen = false,
  emptyLabel = "Niet ingedeeld",
  searchLabel = "Zoek wat het is",
}: {
  value: string | null;
  onChange: (code: string | null) => void;
  full: boolean;
  /** Voorstellen (bv. van de asset of trefwoorden), eerste is het beste. */
  suggestions?: string[];
  /** Laatst gebruikte codes (per gebouw, op dit apparaat). */
  recent?: string[];
  /** Vaak gekozen codes (bij assets: de codes met een eigen symbool). */
  favorites?: string[];
  defaultOpen?: boolean;
  emptyLabel?: string;
  searchLabel?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  /** Gekozen hoofdgroep en element bij het bladeren. */
  const [path, setPath] = useState<string[]>(() =>
    defaultOpen && value ? [value.slice(0, 1), nlsfbGroupCode(value)] : [],
  );
  const [active, setActive] = useState(0);
  const id = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => searchElements(q, { full, limit: 40 }), [q, full]);
  const cur = nlsfb(value);

  const clamp = (codes: string[]) =>
    [...new Set(codes.map((c) => (full ? c : nlsfbGroupCode(c))))].filter((c) => nlsfbNode(c));
  const startPath = () => (value ? [value.slice(0, 1), nlsfbGroupCode(value)] : []);
  const close = () => {
    setOpen(false);
    setQ("");
  };
  const pick = (code: string | null) => {
    onChange(code);
    close();
  };
  const openPanel = () => {
    setPath(startPath());
    setQ("");
    setOpen(true);
  };
  useEffect(() => setActive(0), [q]);

  const name = (code: string) => (assetTypeForCode(code) ? assetNameForCode(code) : elementShortName(code));
  const row = (
    code: string,
    opts: { drill?: boolean; label?: string; sub?: string; selected?: boolean; dim?: boolean; i?: number } = {},
  ) => (
    <button
      key={code + (opts.label ?? "")}
      id={opts.i !== undefined ? `${id}-r${opts.i}` : undefined}
      type="button"
      role={opts.i !== undefined ? "option" : undefined}
      aria-selected={opts.i !== undefined ? opts.i === active : undefined}
      className={`sfbrow${opts.selected ? " on" : ""}${opts.dim ? " dim" : ""}${opts.i === active && opts.i !== undefined ? " act" : ""}`}
      aria-current={code === value || undefined}
      onClick={() => (opts.drill ? (setPath([code.slice(0, 1), code]), setQ("")) : pick(code))}
    >
      <AssetBadge code={code} size={32} />
      <span className="grow">
        <span className="block">{opts.label ?? elementShortName(code)}</span>
        {opts.sub && <span className="muted small block ellipsis">{opts.sub}</span>}
      </span>
      <b className="num sfbcode">{code}</b>
      {opts.drill && (
        <span className="sfbchev" aria-hidden="true">
          ›
        </span>
      )}
    </button>
  );
  const chip = (code: string) => (
    <button key={code} type="button" className="chip chipbtn" onClick={() => pick(code)}>
      <AssetBadge code={code} size={22} />
      {name(code)} <span className="num muted small">{code}</span>
    </button>
  );
  const chipRow = (label: string, codes: string[]) =>
    codes.length > 0 && (
      <div className="catsuggest" role="group" aria-label={label}>
        <span className="muted small">{label}:</span>
        {codes.map(chip)}
      </div>
    );

  const visibleSuggestions = clamp(suggestions)
    .filter((c) => c !== value)
    .slice(0, 3);
  const visibleRecent = clamp(recent)
    .filter((c) => c !== value && !visibleSuggestions.includes(c))
    .slice(0, 3);
  const visibleFavorites = clamp(favorites).filter((c) => c !== value);
  const main = path[0] ? NLSFB.find((m) => m.code === path[0]) : undefined;
  const element = path[1] ? nlsfbNode(path[1]) : undefined;
  const depth = element && full ? 2 : main ? 1 : 0;

  const detailColumn = element && (
    <>
      {row(element.code, { label: `Hele element ${element.code}`, sub: "Als je het detail niet weet" })}
      {nlsfbChildren(element.code).map((g) => {
        const kids = nlsfbChildren(g.code).filter((k) => !isCollective(k));
        if (!kids.length) return /\.0$/.test(g.code) ? null : row(g.code, { dim: isUnnamed(g.label) });
        return (
          <div key={g.code} role="group" aria-label={`${g.code} ${elementShortName(g.code)}`}>
            <button
              type="button"
              className="sfbsec"
              onClick={() => pick(g.code)}
              aria-current={g.code === value || undefined}
            >
              {elementShortName(g.code)} <span className="num">{g.code}</span>
            </button>
            {kids.map((k) =>
              row(k.code, assetTypeForCode(k.code) ? { label: name(k.code), sub: elementShortName(k.code) } : {}),
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="catpicker">
      <div className="catcur">
        <AssetBadge code={value} size={40} />
        <span className="grow">
          <strong>
            {value ? name(value) : emptyLabel} {value && <span className="num muted small">{value}</span>}
          </strong>
          {value && cur.main && <span className="muted small block">{pathAbove(value)}</span>}
        </span>
        {value && !open && (
          <button type="button" className="btn small" onClick={() => onChange(null)}>
            Wissen
          </button>
        )}
        <button type="button" className="btn small" aria-expanded={open} onClick={() => (open ? close() : openPanel())}>
          {open ? "Inklappen" : value ? "Wijzigen" : "Kiezen"}
        </button>
      </div>
      {!open && chipRow("Laatst gebruikt", visibleRecent)}
      {!open && chipRow("Voorstel", visibleSuggestions)}
      {open && (
        <div className="sfb" data-depth={depth} data-full={full || undefined}>
          <input
            ref={searchRef}
            type="search"
            aria-label={searchLabel}
            placeholder={full ? "Zoek, bv. ketel, sprinkler of 56.21" : "Zoek, bv. ketel, verwarming of 56"}
            value={q}
            role="combobox"
            aria-expanded={q.trim().length > 0}
            aria-controls={`${id}-res`}
            aria-activedescendant={q.trim() && hits.length ? `${id}-r${active}` : undefined}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                e.preventDefault();
                if (q) setQ("");
                else close();
              }
              if (!q.trim() || !hits.length) return;
              if (e.key === "ArrowDown") setActive((a) => Math.min(a + 1, hits.length - 1));
              else if (e.key === "ArrowUp") setActive((a) => Math.max(a - 1, 0));
              else if (e.key === "Enter") pick(hits[active]!);
              else return;
              e.preventDefault();
            }}
          />
          {q.trim() ? (
            <div className="sfbres" id={`${id}-res`} role="listbox" aria-label="Zoekresultaten">
              {hits.map((c, i) => row(c, { i, label: name(c), sub: pathAbove(c) || undefined }))}
              {hits.length === 0 && (
                <p className="muted">
                  Niets gevonden. Probeer een ander woord of{" "}
                  <button type="button" className="linkbtn" onClick={() => setQ("")}>
                    blader via de groepen
                  </button>
                  .
                </p>
              )}
            </div>
          ) : (
            <>
              <nav className="sfbcrumbs" aria-label="Pad">
                {depth > 0 && (
                  <button type="button" className="btn small sfbback" onClick={() => setPath(path.slice(0, depth - 1))}>
                    ‹ Terug
                  </button>
                )}
                <button type="button" onClick={() => setPath([])} aria-current={depth === 0 ? "page" : undefined}>
                  Alle groepen
                </button>
                {main && (
                  <>
                    <span aria-hidden="true">›</span>
                    <button
                      type="button"
                      onClick={() => setPath([main.code])}
                      aria-current={depth === 1 ? "page" : undefined}
                    >
                      {main.code} {main.label}
                    </button>
                  </>
                )}
                {element && full && (
                  <>
                    <span aria-hidden="true">›</span>
                    <button type="button" aria-current="page">
                      {element.code} {element.label}
                    </button>
                  </>
                )}
              </nav>
              <div className="sfbquick">
                {chipRow("Laatst gebruikt", visibleRecent)}
                {chipRow("Voorstel", visibleSuggestions)}
                {chipRow("Vaak gekozen", visibleFavorites)}
              </div>
              <div className="sfbcols">
                <div className="sfbcol" data-col="0" role="group" aria-label="Hoofdgroep">
                  <p className="label">1 · Hoofdgroep</p>
                  {NLSFB.map((m) => (
                    <button
                      key={m.code}
                      type="button"
                      className={`sfbrow${path[0] === m.code ? " on" : ""}`}
                      onClick={() => setPath([m.code])}
                    >
                      <span className="sfbmain" style={{ background: m.color }}>
                        {m.code}
                      </span>
                      <span className="grow">
                        <span className="block">{m.label}</span>
                        <span className="muted small block">{m.groups.length} elementen</span>
                      </span>
                      <span className="sfbchev" aria-hidden="true">
                        ›
                      </span>
                    </button>
                  ))}
                </div>
                <div className="sfbcol" data-col="1" role="group" aria-label="Element">
                  <p className="label">2 · Element</p>
                  {main ? (
                    main.groups.map((g) =>
                      row(g.code, {
                        label: g.label,
                        drill: full && nlsfbChildren(g.code).length > 0,
                        selected: path[1] === g.code,
                        dim: isUnnamed(g.label),
                      }),
                    )
                  ) : (
                    <p className="muted small sfbempty">Kies eerst een hoofdgroep.</p>
                  )}
                </div>
                {full && (
                  <div className="sfbcol" data-col="2" role="group" aria-label="Detail">
                    <p className="label">4 · Detail</p>
                    {detailColumn || <p className="muted small sfbempty">Kies eerst een element.</p>}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
